import React, { useEffect, useState } from "react";
import {
  Card,
  Tag,
  Space,
  Select,
  Input,
  Row,
  Col,
  Statistic,
  Button,
  Tooltip,
} from "antd";
import Table, { MAX_PAGE_SIZE } from 'components/Common/Table';
import { EyeOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import useUrlFilters from "hooks/useUrlFilters";
import * as tmActions from "store/actions/TenantManagementActions";
import dayjs from "dayjs";

const STATUS_COLORS = {
  active: "green",
  inactive: "default",
  pending: "orange",
  expired: "red",
  cancelled: "default",
};

// Filters and search live in the URL so the list can be shared.
const URL_FILTERS = {
  app_connection: { type: "string" },
  status: { type: "string" },
  search: { type: "string" },
};

const TenantSubscriptions = () => {
  const navigate = useNavigate();
  const [subscriptions, setSubscriptions] = useState([]);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [urlFilters, setUrlFilters] = useUrlFilters(URL_FILTERS);
  const filters = {
    app_connection: urlFilters.app_connection || undefined,
    status: urlFilters.status || undefined,
    search: urlFilters.search || "",
  };
  const [searchLive, setSearchLive] = useState(filters.search);
  useEffect(() => setSearchLive(filters.search), [filters.search]);

  const fetchSubscriptions = async () => {
    setLoading(true);
    const params = { page_size: MAX_PAGE_SIZE }; // one full page; the table pages client-side
    if (filters.app_connection) params.app_connection = filters.app_connection;
    if (filters.status) params.status = filters.status;
    if (filters.search) params.search = filters.search;
    const data = await tmActions.loadSyncedSubscriptions(params);
    if (data) setSubscriptions(Array.isArray(data) ? data : data.results || []);
    setLoading(false);
  };

  const fetchConnections = async () => {
    const data = await tmActions.loadAppConnections();
    if (data) setConnections(Array.isArray(data) ? data : data.results || []);
  };

  useEffect(() => {
    fetchConnections();
  }, []);

  useEffect(() => {
    fetchSubscriptions();
  }, [filters.app_connection, filters.status, filters.search]);

  const handleSearch = (value) => {
    if (value === filters.search) fetchSubscriptions();
    else setUrlFilters({ search: value });
  };

  const stats = {
    total: subscriptions.length,
    active: subscriptions.filter((s) => s.status === "active").length,
    pending: subscriptions.filter((s) => s.status === "pending").length,
    expired: subscriptions.filter((s) => s.status === "expired").length,
  };

  const columns = [
    {
      title: "Plan",
      dataIndex: "plan_name",
      key: "plan_name",
      render: (text, record) => (
        <a onClick={() => navigate(`/tenant-management/subscriptions/${record.id}`)}>{text || "-"}</a>
      ),
    },
    {
      title: "App",
      dataIndex: "app_connection_name",
      key: "app_connection_name",
      render: (text) => <Tag>{text}</Tag>,
    },
    {
      title: "Tenant",
      dataIndex: "tenant_name",
      key: "tenant_name",
      render: (text, record) =>
        text ? (
          <a onClick={() => navigate(`/tenant-management/tenants/${record.synced_tenant}`)}>{text}</a>
        ) : (
          "-"
        ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (s) => <Tag color={STATUS_COLORS[s] || "default"}>{s}</Tag>,
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (a) => `₹${parseFloat(a).toLocaleString()}`,
    },
    {
      title: "Start Date",
      dataIndex: "start_date",
      key: "start_date",
      render: (d) => d || "-",
    },
    {
      title: "End Date",
      dataIndex: "end_date",
      key: "end_date",
      render: (d) => d || "-",
    },
    {
      title: "Last Synced",
      dataIndex: "last_synced_at",
      key: "last_synced_at",
      render: (d) => d ? dayjs(d).format("YYYY-MM-DD HH:mm") : "-",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Tooltip title="View Details">
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/tenant-management/subscriptions/${record.id}`)}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <div style={{ padding: "16px" }}>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card><Statistic title="Total" value={stats.total} /></Card>
        </Col>
        <Col span={6}>
          <Card><Statistic title="Active" value={stats.active} valueStyle={{ color: "#3f8600" }} /></Card>
        </Col>
        <Col span={6}>
          <Card><Statistic title="Pending" value={stats.pending} valueStyle={{ color: "#faad14" }} /></Card>
        </Col>
        <Col span={6}>
          <Card><Statistic title="Expired" value={stats.expired} valueStyle={{ color: "#cf1322" }} /></Card>
        </Col>
      </Row>

      <Card title="All Subscriptions">
        <Space style={{ marginBottom: 16 }} wrap>
          <Select
            placeholder="Filter by App"
            allowClear
            style={{ width: 200 }}
            value={filters.app_connection}
            onChange={(val) => setUrlFilters({ app_connection: val })}
            options={connections.map((c) => ({ value: String(c.id), label: c.product_name }))}
          />
          <Select
            placeholder="Filter by Status"
            allowClear
            style={{ width: 160 }}
            value={filters.status}
            onChange={(val) => setUrlFilters({ status: val })}
            options={[
              { value: "active", label: "Active" },
              { value: "inactive", label: "Inactive" },
              { value: "pending", label: "Pending" },
              { value: "expired", label: "Expired" },
              { value: "cancelled", label: "Cancelled" },
            ]}
          />
          <Input.Search
            placeholder="Search subscriptions..."
            style={{ width: 250 }}
            value={searchLive}
            onChange={(e) => setSearchLive(e.target.value)}
            onSearch={handleSearch}
            enterButton
          />
        </Space>
        <Table
          dataSource={subscriptions}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={{ defaultPageSize: 20, showSizeChanger: true }}
        />
      </Card>
    </div>
  );
};

export default TenantSubscriptions;
